<template>
    <div class="w-full max-w-md p-8">
        <h1 class="font-[700] text-3xl text-center text-title mb-[1vh]">{{
            $t('pages.signup.step1.title')
            }}</h1>
        <p class="font-[600] text-base text-center align-middle text-body mb-[2vh]">{{
            $t('pages.signup.step1.subtitle') }}
        </p>

        <UForm method="post" :state="formState" :schema="schema" @submit="onSubmit" class="space-y-4">
            <UFormField name="email" label="Email">
                <UInput class="w-full" size="xl" v-model="formState.email" type="email"
                    :placeholder="$t('pages.signup.step1.form.email.placeholder')" :disabled="isLoading"
                    :ui="{ base: 'py-3 px-4' }" />
            </UFormField>
            <p v-if="errorMessages" class="text-error text-sm">{{ errorMessages }}</p>

            <div>
                <div class="flex items-center my-4">
                    <div class="flex-grow border-t border-default"></div>
                    <span class="mx-2 font-[700] text-lg tracking-tight text-title">{{ $t('pages.signup.step1.or')
                    }}</span>
                    <div class="flex-grow border-t border-default"></div>
                </div>
                <!-- Google 登入按鈕 -->
                <UButton type="button" color="neutral" variant="outline" size="xl" block :disabled="isLoading"
                    @click="handleGoogleLogin" class="mb-[3vh] py-3">
                    <div class="flex items-center justify-center gap-2">
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google"
                            class="w-5 h-5" />
                        <div class="flex items-center justify-center gap-1">
                            <div>{{ $t('pages.signup.step1.continueWithGoogle.continue') }}</div>
                            <div>{{ $t('pages.signup.step1.continueWithGoogle.with') }}</div>
                            <div>{{ $t('pages.signup.step1.continueWithGoogle.google') }}</div>
                        </div>
                    </div>
                </UButton>
                <!-- 邀請碼輸入 Modal -->
                <UModal v-model:open="showInviteModal" :dismissible="false" :ui="{
                    content: 'max-w-[600px] w-[100vw]'
                }">
                    <template #content>
                        <div class="px-24 py-16">
                            <div class="flex flex-col gap-2 mb-12">
                                <h2 class="font-[700] text-3xl text-center text-title">
                                    {{ $t('pages.signup.step1.inviteModal.title') }}
                                </h2>
                                <p class="font-[600] text-base text-center align-middle text-body">
                                    {{ $t('pages.signup.step1.inviteModal.subtitle') }}
                                </p>
                            </div>
                            <UForm method="post" :state="inviteFormState" :schema="inviteSchema" @submit="onInviteContinue"
                                class="space-y-4">
                                <UFormField name="inviteCodeForSocial" label="Invitation Code" class="h-24">
                                    <UInput class="w-full" size="xl" v-model="inviteFormState.inviteCodeForSocial"
                                        type="text" :placeholder="$t('pages.signup.step1.form.invitation.placeholder')"
                                        :ui="{ base: 'py-3 px-4' }" />
                                </UFormField>

                                <div class="flex flex-col gap-4">
                                    <UButton type="submit" :disabled="!isHydrated" color="primary" size="xl" block class="py-3">
                                        {{ $t('pages.signup.step1.continue') }}
                                    </UButton>
                                    <UButton type="button" color="neutral" variant="outline" size="xl" block
                                        class="py-3" @click="closeInviteModal">
                                        {{ $t('pages.signup.step1.inviteModal.back') }}
                                    </UButton>
                                </div>
                            </UForm>
                        </div>
                    </template>
                </UModal>
            </div>

            <UButton type="submit" :disabled="!isHydrated" color="primary" size="xl" block :loading="isLoading" class="py-3">
                {{ $t('pages.signup.step1.sendVerificationEmail') }}
            </UButton>

            <p class="text-center text-sm font-bold">
                {{ $t('pages.signup.step1.alreadyHaveAccount') }}
                <template v-if="isLoading">
                    <span class="text-body font-bold ml-1">{{ $t('pages.signup.step1.login') }}</span>
                </template>
                <template v-else>
                    <NuxtLink :to="localePath('/login')"
                        class="text-interactive hover:underline font-bold cursor-pointer ml-1">
                        {{ $t('pages.signup.step1.login') }}
                    </NuxtLink>
                </template>
            </p>
        </UForm>
    </div>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui';
import { useMounted } from '@vueuse/core';
import * as yup from 'yup';
import { getConfigsApi } from '~/api/app';
import { checkEmailRegisteredApi, sendRegisterCodeApi } from '~/api/auth';
import { useAuth } from '~/composables/useAuth';
import { useRouter } from '#app';
import { NuxtLink } from '#components';
import { useLocalePath } from '#i18n';

const router = useRouter();
const localePath = useLocalePath();
const { handleSocialLogin, login } = useAuth();
const { t } = useI18n();
const isLoading = ref(false);
// hydration 完成前不讓表單送得出去，避免瀏覽器走原生提交把欄位帶進網址列
const isHydrated = useMounted();

const emit = defineEmits(['toStep2']);

const formState = ref({
  email: '',
});

const inviteFormState = ref({
  inviteCodeForSocial: '',
});

const schema = yup.object({
  email: yup
    .string()
    .required(t('pages.signup.step1.validation.email'))
    .email(t('pages.signup.step1.validation.email')),
});

const inviteSchema = yup.object({
  inviteCodeForSocial: yup.string().required(t('pages.signup.step1.validation.invitation')),
});

const errorMessages = ref('');

async function onSubmit(event: FormSubmitEvent<typeof formState.value>) {
  try {
    isLoading.value = true;
    const isRegistered = await checkEmailRegisteredApi(event.data.email);
    if (isRegistered) {
      errorMessages.value = t('pages.signup.step1.validation.email_already_registered');
      return;
    }
    sendRegisterCodeApi(event.data.email);
    emit('toStep2', event.data.email);
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
}

const showInviteModal = ref(false);
const requiresInvitation = ref(true); // 預設需要邀請碼（較安全的預設值）

// 獲取系統配置，判斷是否需要邀請碼
const loadConfig = async () => {
  try {
    const config = await getConfigsApi();
    // 如果 is_open_invite 為 true，表示需要邀請碼
    // 如果 is_open_invite 為 false，表示不需要邀請碼（開放註冊）
    requiresInvitation.value = config.is_open_invite;
  } catch (error) {
    // 如果 API 調用失敗，預設需要邀請碼（較安全的預設值）
    console.warn('無法載入系統配置，預設需要邀請碼:', error);
    requiresInvitation.value = true;
  }
};

// 處理 Google 登入按鈕點擊
const handleGoogleLogin = () => {
  // 如果不需要邀請碼，直接進行 Google 登入
  if (!requiresInvitation.value) {
    handleSocialLogin();
  } else {
    // 如果需要邀請碼，顯示 modal 讓用戶輸入邀請碼
    openInviteModal();
  }
};

const openInviteModal = () => {
  showInviteModal.value = true;
};

const closeInviteModal = () => {
  showInviteModal.value = false;
};

async function onInviteContinue(event: FormSubmitEvent<typeof inviteFormState.value>) {
  showInviteModal.value = false;
  handleSocialLogin(event.data.inviteCodeForSocial);
}

onMounted(async () => {
  // 載入配置以判斷是否需要邀請碼
  await loadConfig();
});
</script>